/**
 * Where OPTIMA lives in the shell (bata-platform epic 719, phase F1, ADR 9080; the request intake
 * of bata-platform ADR 2137, ADR 9071).
 *
 * One provider, mounted once above the chrome, like the problem report's. It owns what has to
 * outlive the panel being opened and closed:
 *
 *   · whether the assistant is on at all (`GET /assistant/status`) — until it says `enabled` the
 *     shell is exactly what it was: no button, no shortcut, no panel;
 *   · the conversation, **in memory only**: a ref-less `useState`, never `localStorage`,
 *     `sessionStorage` or the URL. Reloading the page clears it; closing the panel does not. Since
 *     the request intake the platform keeps the conversation too, by the `conversation_id` its
 *     first answer names: every later question carries it, so OPTIMA can ask for what a report
 *     still needs. «Nuova conversazione» ends it here, and a conversation the platform has closed
 *     (too many turns, idle too long) is replaced by a new one on the next question, carrying
 *     nothing over;
 *   · what became of each draft the person confirmed («Sì, invia la segnalazione»), so closing and
 *     reopening the panel shows the report's code rather than the button again;
 *   · the one question in flight, and the failure of the last one;
 *   · the shortcut (Alt+O) that opens and closes the panel from anywhere, grid included;
 *   · what the avatar is doing (`avatarStateOf`, ADR 9077), which the entry in the bar and the
 *     panel's header both draw: the shoe comes out of the box when the panel opens, reads while a
 *     question is in flight, and goes back in 600 ms after a report is filed or when the panel closes.
 *
 * It sends the platform what the platform needs to answer about THIS screen — the path (without
 * its query, which names runs and stores), the screen's title and the person's language — and
 * nothing about what is on the screen. The runs in the address go only into a filing's context,
 * which the platform keeps with the report and never passes to the model.
 *
 * A person who is not inside a provider (a test that mounts the chrome alone) gets `null` from
 * {@link useOptima}, and the chrome draws no entry: an entry that opens nothing is worse than none.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { recentCalls } from '../../api';
import { APP_VERSION } from '../../lib/build-info';
import { useShellAccess } from '../feedback/access';
import { FEEDBACK_LIMITS } from '../feedback/api';
import { useFeedback } from '../feedback/FeedbackProvider';
import { screenScope } from '../feedback/screen-scope';
import { FEEDBACK_PATH } from '../routes/feedback/constants';

import { useAskAssistant, useAssistantStatus, useFileDraft } from './api';
import {
  RETURN_TO_BOX_MS, avatarStateOf, currentDraftExchange, draftOf, draftPrefill, isConversationClosed, isOptimaShortcut,
  type Exchange, type Filing, type OptimaState,
} from './optima';
import { OptimaPanel } from './OptimaPanel';
import { screenTitle } from './screen-title';

export interface OptimaControls {
  /** The assistant is on for this deployment and the person may use it. */
  readonly enabled: boolean;
  readonly open: boolean;
  /** What the avatar is doing, from the conversation's real lifecycle (`avatarStateOf`); `pronto` while the panel is closed. */
  readonly avatarState: OptimaState;
  readonly toggle: () => void;
  /** For the entry that opens the panel: the panel hands the focus back to it when it closes. */
  readonly triggerRef: React.RefObject<HTMLButtonElement | null>;
}

const OptimaContext = createContext<OptimaControls | null>(null);

/** The panel's controls, or `null` where no provider is mounted. */
export function useOptima(): OptimaControls | null {
  return useContext(OptimaContext);
}

export interface OptimaProviderProps {
  /** The path the person is on. */
  readonly currentPath: string;
  /** The address's parameters: the runs a filing's context carries (never sent to the assistant). */
  readonly params?: Readonly<Record<string, string>>;
  /** Opens «My reports» from a filed draft's receipt. Without it the receipt names the code only. */
  readonly onNavigate?: (to: string) => void;
  readonly children: React.ReactNode;
}

/** The filing context's bound on run ids (ADR 2137: at most five, as the correlation ids). */
const MAX_RUN_IDS = 5;

/** A run id the filing accepts (the platform's `RUN_ID_PATTERN`): one it would refuse must not cost the person their report. */
const RUN_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/;

export function OptimaProvider({ currentPath, params, onNavigate, children }: OptimaProviderProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const access = useShellAccess();
  const status = useAssistantStatus();
  const ask = useAskAssistant();
  const fileDraft = useFileDraft();
  const feedback = useFeedback();

  // Strictly `true`: an answer of any other shape (a stand-in, an older platform's page) is «off».
  const enabled = status.data?.enabled === true;
  // The same rule for the intake: a platform before it sends no `intake`, and the panel is F1's.
  const intake = enabled && status.data?.intake === true;

  const [open, setOpen] = useState(false);
  const [exchanges, setExchanges] = useState<readonly Exchange[]>([]);
  const [filings, setFilings] = useState<Readonly<Record<number, Filing>>>({});
  // The exchange whose report was filed and whose 600 ms have passed: the shoe is back in the box.
  const [returnedFor, setReturnedFor] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const nextId = useRef(0);
  const inFlight = useRef(false);
  // The platform's conversation. A ref, not state: nothing is drawn from it, and `submit` reads the
  // newest value without being re-bound on every answer.
  const conversation = useRef<string | null>(null);

  const toggle = useCallback(() => setOpen((was) => !was), []);

  /** Closes the panel. Escape and the shortcut hand the focus back to the entry that opened it;
   *  the handoff does not, because the report's form takes it (and a non-modal dialog closes the
   *  moment focus lands elsewhere). */
  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) window.setTimeout(() => triggerRef.current?.focus(), 0);
  }, []);

  // The shortcut reads the state through a ref so the listener is bound once per `enabled`.
  const openNow = useRef(false);
  useEffect(() => { openNow.current = open; }, [open]);

  // Alt+O, bound on the window so it works while focus is in the grid — where the person is when
  // a figure raises the question. Ctrl/⌘ K is the command palette's and is left alone.
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!isOptimaShortcut(event)) return;
      event.preventDefault();
      if (openNow.current) close(true);
      else setOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, close]);

  // The assistant switched off while the panel was open (the status refetched): close it.
  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  const submit = useCallback(async (question: string, existingId?: number): Promise<void> => {
    const text = question.trim();
    if (text === '' || inFlight.current) return;
    inFlight.current = true;

    let id = existingId;
    if (id === undefined) {
      nextId.current += 1;
      id = nextId.current;
      const created: Exchange = { id, question: text, state: 'pending' };
      setExchanges((shown) => [...shown, created]);
    } else {
      const retried = id;
      setExchanges((shown) => shown.map((e) => (e.id === retried ? { id: e.id, question: e.question, state: 'pending' } : e)));
    }
    const exchangeId = id;
    const title = screenTitle(currentPath, access, t);
    const continuing = conversation.current;
    try {
      const turn = await ask({
        question: text,
        locale: i18n.language,
        context: { route: currentPath, ...(title === undefined ? {} : { title }) },
        ...(continuing === null ? {} : { conversationId: continuing }),
      });
      if (typeof turn.conversation_id === 'string' && turn.conversation_id !== '') conversation.current = turn.conversation_id;
      setExchanges((shown) => shown.map((e) => (e.id === exchangeId ? { id: e.id, question: e.question, state: 'answered', turn } : e)));
    } catch (error) {
      // A closed conversation is not resumed: the next question, or this one again, opens a new one.
      if (isConversationClosed(error)) conversation.current = null;
      setExchanges((shown) => shown.map((e) => (e.id === exchangeId ? { id: e.id, question: e.question, state: 'failed', error } : e)));
    } finally {
      inFlight.current = false;
    }
  }, [ask, access, currentPath, i18n.language, t]);

  /** «Nuova conversazione»: the panel forgets the conversation; the platform closes its own by its clock. */
  const newConversation = useCallback(() => {
    if (inFlight.current) return;
    conversation.current = null;
    setExchanges([]);
    setFilings({});
  }, []);

  /**
   * «Sì, invia la segnalazione» — the person's confirmation, and the only way the panel files. It
   * sends the platform's single-use token and the digest of the draft drawn on the card; the
   * platform checks both, and the person, before it files anything. A typed «sì» is a question like
   * any other: nothing in the panel reads the text of a question.
   */
  const file = useCallback(async (exchangeId: number): Promise<void> => {
    const exchange = exchanges.find((e) => e.id === exchangeId);
    const current = draftOf(exchange?.turn);
    const confirmation = current?.confirmation ?? null;
    const id = exchange?.turn?.conversation_id ?? conversation.current;
    if (current === null || confirmation === null || id === undefined || id === null) return;
    const was = filings[exchangeId]?.state;
    if (was === 'sending' || was === 'filed') return;
    setFilings((shown) => ({ ...shown, [exchangeId]: { state: 'sending' } }));
    try {
      const receipt = await fileDraft({
        conversationId: id,
        draftDigest: confirmation.draft_digest,
        confirmation: confirmation.token,
        context: {
          route: currentPath.slice(0, FEEDBACK_LIMITS.route.max),
          app_version: APP_VERSION.slice(0, FEEDBACK_LIMITS.appVersion.max),
          locale: i18n.language.slice(0, FEEDBACK_LIMITS.locale.max),
          correlation_ids: recentCalls().map((call) => call.correlationId)
            .filter((value) => FEEDBACK_LIMITS.correlationIds.pattern.test(value))
            .slice(-FEEDBACK_LIMITS.correlationIds.max),
          run_ids: screenScope(params).runs.filter((run) => RUN_ID.test(run)).slice(0, MAX_RUN_IDS),
        },
      });
      setFilings((shown) => ({ ...shown, [exchangeId]: { state: 'filed', receipt } }));
    } catch (error) {
      setFilings((shown) => ({ ...shown, [exchangeId]: { state: 'failed', error } }));
    }
  }, [exchanges, filings, fileDraft, i18n.language, params, currentPath]);

  /** «Apri nel modulo» — the report form, pre-filled with the whole draft, for the person to edit and send. */
  const openInForm = useCallback((exchangeId: number): void => {
    const exchange = exchanges.find((e) => e.id === exchangeId);
    const current = draftOf(exchange?.turn);
    if (current === null || feedback === null) return;
    feedback.open(draftPrefill(current.draft, exchange?.turn?.conversation_id ?? conversation.current));
    // The report's form takes the focus; the conversation is kept for when the panel opens again.
    setOpen(false);
  }, [exchanges, feedback]);

  const openMyReports = useMemo(() => (onNavigate === undefined ? undefined : () => {
    onNavigate(FEEDBACK_PATH);
    setOpen(false);
  }), [onNavigate]);

  // The shoe goes back into the box 600 ms after the report is filed (LEGGIMI «Rientro»).
  const draftExchange = currentDraftExchange(exchanges);
  const filedExchange = draftExchange !== null && filings[draftExchange]?.state === 'filed' ? draftExchange : null;
  useEffect(() => {
    if (filedExchange === null) return undefined;
    const timer = window.setTimeout(() => setReturnedFor(filedExchange), RETURN_TO_BOX_MS);
    return () => window.clearTimeout(timer);
  }, [filedExchange]);

  const avatarState = avatarStateOf({
    open: enabled && open, exchanges, filings, returned: filedExchange !== null && returnedFor === filedExchange,
  });

  const controls = useMemo<OptimaControls>(() => ({ enabled, open, avatarState, toggle, triggerRef }), [enabled, open, avatarState, toggle]);

  return (
    <OptimaContext.Provider value={controls}>
      {children}
      {enabled && open && (
        <OptimaPanel
          exchanges={exchanges}
          busy={exchanges.some((e) => e.state === 'pending')}
          avatarState={avatarState}
          intake={intake}
          filings={filings}
          onAsk={(question) => { void submit(question); }}
          onRetry={(id, question) => { void submit(question, id); }}
          onClose={close}
          onNewConversation={newConversation}
          onFile={(id) => { void file(id); }}
          {...(feedback === null ? {} : { onOpenInForm: openInForm })}
          {...(openMyReports === undefined ? {} : { onOpenMyReports: openMyReports })}
        />
      )}
    </OptimaContext.Provider>
  );
}
